/**
 * ----------------------------------------------------------------------------
 * flows/shared/selectors.js (public API)
 * ----------------------------------------------------------------------------
 *
 * This file contains utils and selectors that operate on flux state as it
 * relates to the Logic Flow feature. These selectors can be used anywhere
 * flux.state is available, such as our flux engines, reducers etc, and also in
 * conjunction with the useDesignerSelector React custom hook:
 *
 * ```js
 * // In packages...
 * import {useDesignerSelector} from '@packages/systems/designer/flux';
 *
 * const MyComponent = () => {
 *   const flow = useDesignerSelector(selectSelectedFlow);
 *   ...
 * }
 *
 * Add new useful selectors here to avoid unnecessary duplication, and make
 * use of the various memoization functions like `createSelector` and `weakMemo`
 * to cache results.
 */

// ----------------------------------------------------------------------------
// Helper imports — try not to import too many modules please 🙏🏼🙏🏼🙏🏼
// ----------------------------------------------------------------------------

import {createSelector} from 'reselect';
import {getParameters} from '@packages/systems/panels';
import identity from 'lodash/identity';
// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

// We're importing directly from the types.js file to avoid circular dependencies.

import type {LogicTriggersType} from '@packages/systems/flows/types';
import type {DesignerStores} from '@packages/systems/designer/flux';

// ----------------------------------------------------------------------------
// Selectors
// ----------------------------------------------------------------------------

/**
 * Selects the current flowId in the flow editor.
 */
export const selectCurrentFlowId = createSelector(
  (state: DesignerStores) => getParameters(state.PanelsStore),
  (parameters) => parameters?.flowParam?.flowId
);

/**
 * Selects logicTriggers and returns a collection of getters that work
 * with logic trigger meta data.
 */
export const selectLogicTriggersMethods = createSelector(
  (state: DesignerStores) => state.DesignerStore.logicTriggers,
  (logicTriggers: LogicTriggersType) => {
    return {
      getLogicTriggers: identity,
      getLogicTriggerByFlowId: (flowId: string) => logicTriggers[flowId],
    };
  }
);

/**
 * Selects the webhook trigger metadata if the flow trigger is webhook-based.
 */
export const selectWebhook = createSelector(
  selectLogicTriggersMethods,
  selectCurrentFlowId,
  ({getLogicTriggerByFlowId}, flowId) =>
    (flowId && getLogicTriggerByFlowId(flowId)?.webhook) || undefined
);

/**
 * Selects the collection_item_updated trigger metadata if the flow trigger.
 */
export const selectCmsItemUpdatedTrigger = createSelector(
  selectLogicTriggersMethods,
  selectCurrentFlowId,
  ({getLogicTriggerByFlowId}, flowId) =>
    (flowId && getLogicTriggerByFlowId(flowId)?.collection_item_updated) ||
    undefined
);

/**
 * Selects the list of secrets that users have created for their flows.
 */
export const selectCredentials = (state: DesignerStores) =>
  state.CredentialsStore;
